/** The transparent screen cut-out of a device-frame PNG, as % of the PNG. Slightly
 *  overscanned so the preview fully covers the opening; the excess hides under the
 *  opaque bezel.
 *
 *  Overscan the HORIZONTAL edges by ~1px of the PNG and no more. The previewed site is
 *  laid out at a fixed virtual width (1280 desktop) and scaled down into this box, so
 *  every overscanned PNG pixel swallows ~1.5 CSS px of the site's right edge — where a
 *  modal's close button lives. The values below are measured from each PNG's alpha
 *  channel (scan the centre row outward to the first opaque pixel); do not eyeball them. */
export type ScreenInset = {
  readonly left: string;
  readonly top: string;
  readonly width: string;
  readonly height: string;
};

/**
 * A device-frame artwork: a PNG with a transparent screen cut-out, its aspect ratio
 * (the PNG's w/h, also the device box ratio), and that cut-out as % of the PNG.
 *
 * Lives in its own (dependency-light) module so the persistent preview host can read
 * the laptop artwork without pulling in the wizard component barrel (which would
 * close an import cycle through the host).
 */
export type DeviceFrameArt = {
  readonly src: string;
  readonly aspectRatio: string;
  readonly screen: ScreenInset;
};

/** Default laptop frame — the shared wizard artwork (1113×723).
 *  Measured hole: left 11.680%, width 76.550% (1px = 0.090%). */
export const LAPTOP_FRAME_DEFAULT: DeviceFrameArt = {
  src: 'laptop-frame.png',
  aspectRatio: '1113 / 723',
  screen: { left: '11.590%', top: '11%', width: '76.730%', height: '78%' },
};

/** Tighter-cropped laptop frame for the post-payment success screen (1110×660, no
 *  baked-in top space). Same horizontal hole as the default frame: left 11.680%,
 *  width 76.550%. The vertical overscan is deliberate — the excess tucks under the bezel. */
export const LAPTOP_FRAME_FINISH: DeviceFrameArt = {
  src: 'finish-laptop-frame.png',
  aspectRatio: '1110 / 660',
  screen: { left: '11.590%', top: '3%', width: '76.730%', height: '85%' },
};

/** Phone frame — the shared wizard artwork (792×1619). Measured hole: left 5.429%,
 *  width 89.141% (already exact, no horizontal overscan). Top/height ARE overscanned:
 *  the status + home-indicator strips deliberately tuck under the bezel.
 *
 *  The single source for the phone geometry: both the bezelled `PhoneDevice` and the
 *  unified `MorphDevice` read it, so re-measuring the PNG can never leave one of them
 *  aligned and the other not. */
export const PHONE_FRAME_DEFAULT: DeviceFrameArt = {
  src: 'phone-frame.png',
  aspectRatio: '792 / 1619',
  screen: { left: '5.429%', top: '2.532%', width: '89.141%', height: '94.873%' },
};

/** Phone screen corner radius as a fraction of the device width (scales with the phone). */
export const PHONE_RADIUS_RATIO = 0.112;

/** Chrome-strip heights as a % of the phone screen. Desktop has no bottom strip. */
export const PHONE_STRIP_H = { top: '6.3%', bottom: '4.2%' } as const;
